import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@wfp/ui';
import * as msal from '@azure/msal-browser';

import { getTokenRedirect, msalApp, setMsalAccount, signIn } from './msal';
import { djSessionId } from './restapi';


const AuthState = {
	Authenticated: 'Authenticated',
	UnAuthenticated: 'UnAuthenticated',
	InProgress: 'InProgress',
};

interface Props extends React.PropsWithChildren {
	forceLogin?: boolean;
}

const AuthProviderComponent: React.FunctionComponent<Props> = (props) => {
	const [account, setAccount] = useState<msal.AccountInfo | null>(null);
	const [authState, setAuthState] = useState(AuthState.UnAuthenticated);
	const [accessToken, setAccessToken] = useState<string | null>(null);
	const [error, setError] = useState(null);

	async function login() {
		if (authState !== AuthState.UnAuthenticated) {
			return;
		}
		setAuthState(AuthState.InProgress);
		await signIn();
	}

	const loginCallBack = useCallback(
		() => {
			if (authState !== AuthState.UnAuthenticated) {
				return;
			}
			setAuthState(AuthState.InProgress);
		},
		[authState],
	);

	useEffect(() => {
		if (authState === AuthState.InProgress) {
			setTimeout(() => {
				getTokenRedirect().then(tokenResponse => {
					if (tokenResponse) {
						setAccount(tokenResponse.account);
						setAuthState(AuthState.Authenticated);
						setAccessToken(tokenResponse.accessToken);
					}
				}).catch((e: Error) => {
					console.warn(e);
					console.info('Can\'t find auth token. Call re-login.');
					signIn();
				});
			}, 500);
		}
	}, [authState]);

	useEffect(() => {
		function handleResponse(tokenResponse: msal.AuthenticationResult | null) {
			if (tokenResponse && tokenResponse.account) {
				setMsalAccount(tokenResponse.account);
				setAccount(tokenResponse.account);
				setAuthState(AuthState.Authenticated);
				setAccessToken(tokenResponse.accessToken);
			} else if (props.forceLogin && !error && authState === AuthState.UnAuthenticated) {
				loginCallBack();
			}
		}

		msalApp.initialize().then(() => {
			msalApp.handleRedirectPromise().then(handleResponse);
		}).catch(e => {
			setAuthState(AuthState.UnAuthenticated);
			setError(e.errorMessage);
			console.error('MSAL error happened', e);
		});
	}, [authState, error, props.forceLogin, loginCallBack]);

	useEffect(() => {
		if (account && !import.meta.env.PROD) {
			console.info(`Welcome ${account.name}`);
		}
	}, [account]);

	useEffect(() => {
		if (accessToken && !import.meta.env.PROD) {
			// console.info('Access Token set to', accessToken);
		}
	}, [accessToken]);

	if (error) {
		return <div>Auth Error: {error} <Button kind="primary" onClick={login}>login</Button></div>;
	}
	if (authState === AuthState.Authenticated) {
		return (<>{props.children}</>);
	}
	if (authState === AuthState.UnAuthenticated && !props.forceLogin) {
		return <div><Button kind="primary" onClick={login}>Login</Button></div>;
	}
	return <div>Initializing Application...</div>;
}


const IdentityComponent: React.FunctionComponent<Props> = (props) => {
	return (<>{props.children}</>);
}

// prevent this from being used in tests
export const AuthProvider = (djSessionId || import.meta.env.TEST_MODE) ?
	IdentityComponent : AuthProviderComponent;
